import { FC, useState, useEffect } from 'react'
import { TopBar } from '@reapit/elements'
import { NavigationMenu } from '../types'
import { MainMenuItem } from './MainMenuItem'
import { MainSubMenuItem } from './MainSubMenuItem'
import { DESKTOP_BREAKPOINT_MAX, DESKTOP_BREAKPOINT_MIN, LIMITED_MENU_COUNT } from '../constants'

interface MainMenuProps {
  menu: NavigationMenu[]
}

/**
 * Renders the main navigation menu with top-level and overflow items.
 *
 * This function:
 * - Filters out inaccessible items (`hasAccess: false`)
 * - Slices the visible items based on `maxVisibleMenuItems`
 * - Renders the rest inside a "More" dropdown
 *
 * @param menu - Array of navigation items with access control and metadata
 * @param maxVisibleMenuItems - Maximum number of menu items to display before overflowing
 * @returns JSX.Element - A React element containing the menu structure
 */
const renderMainMenuItems = (menu: NavigationMenu[], maxVisibleMenuItems: number) => {
  const accessibleMenu = menu.filter((item) => item.hasAccess)
  const visibleItems = accessibleMenu.slice(0, maxVisibleMenuItems)
  const moreMainMenuItems = accessibleMenu.slice(maxVisibleMenuItems)

  return (
    <>
      {visibleItems.map((navMenu) => (
        <MainMenuItem key={navMenu.itemIndex} {...navMenu} />
      ))}
      {moreMainMenuItems.length > 0 && (
        <TopBar.NavMenuItem label="More">
          {moreMainMenuItems.map((navMenu) => (
            <MainSubMenuItem key={navMenu.itemIndex} {...navMenu} />
          ))}
        </TopBar.NavMenuItem>
      )}
    </>
  )
}

/**
 * `MainMenu` is a component that renders a list of navigation items.
 * Each item is clickable and executes the corresponding callback when clicked.
 *
 * It dynamically determines how many top-level menu items to display based on the
 * current screen width and moves any overflow items into a "More" dropdown.
 *
 * Props:
 * @param menu (NavigationMenu[]): An array of navigation menu items,
 * each containing an `itemIndex`, `title`, and `callback`.
 *
 * This component:
 * 1. Filters the menu items by `hasAccess`.
 * 2. Renders a responsive number of top-level menu items based on screen width:
 *    - If screen width is between 1440px and 1919px, displays up to 5 items.
 *    - Otherwise, displays all items.
 * 3. If there are more items than can be shown, the overflow items are grouped under a "More" submenu.
 */
export const MainMenu: FC<MainMenuProps> = ({ menu }) => {
  const [maxVisibleMenuItems, setMaxVisibleMenuItems] = useState<number>(() => {
    const width = typeof window !== 'undefined' ? window.innerWidth : 0
    return width >= DESKTOP_BREAKPOINT_MIN && width < DESKTOP_BREAKPOINT_MAX ? LIMITED_MENU_COUNT : Infinity
  })

  /**
   * `useEffect` to handle window resize events.
   * It updates the `maxVisible` state based on the current window width.
   * - If the width is between 1440px and 1919px, sets `maxVisible` to 5.
   * - Otherwise, sets it to Infinity to show all items.
   */
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth
      const newMaxVisibleMenuItem =
        width >= DESKTOP_BREAKPOINT_MIN && width < DESKTOP_BREAKPOINT_MAX ? LIMITED_MENU_COUNT : Infinity

      // Avoid unnecessary state update
      setMaxVisibleMenuItems((prev) => {
        if (prev !== newMaxVisibleMenuItem) {
          return newMaxVisibleMenuItem
        }
        return prev
      })
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  return <TopBar.MainNav>{renderMainMenuItems(menu, maxVisibleMenuItems)}</TopBar.MainNav>
}
